import { z } from "zod";
import { buildingFacilities } from "./attributes";
import { conditionTypes } from "./attributes";
import { parkingTypes } from "./attributes";
import { petsAllowedTypes } from "./attributes";
import { propertyTypes } from "./attributes";
import { tenantTypes } from "./attributes";
import { furnishingTypes } from "../definitions";

export const listingSortOptions = [
  "recommended",
  "date-asc",
  "date-desc",
  "price-asc",
  "price-desc",
  "size-asc",
  "size-desc",
] as const;

export const listingFilterSchema = z.object({
  search: z.string().optional().catch(undefined),
  page: z.coerce.number().int().min(1).optional().default(1).catch(1),
  pageSize: z.coerce
    .number()
    .int()
    .min(1)
    .max(100)
    .optional()
    .default(24)
    .catch(24),
  sort: z.enum(listingSortOptions).optional().default("recommended"),
  ownerId: z.string().min(1).optional(),
  cityId: z.string().min(1).optional(),
  city: z.string().optional().catch(undefined),
  province: z.string().optional().catch(undefined),
  borough: z.string().optional().catch(undefined),
  neighborhood: z.string().optional().catch(undefined),
  street: z.string().optional().catch(undefined),
  createdAtMin: z.preprocess((val) => {
    if (typeof val === "string") return new Date(val);
    return val;
  }, z.date().optional()),
  isAvailableNow: z.preprocess((val) => {
    if (typeof val === "boolean") return val;
    if (val === "true") return true;
    if (val === "false") return false;
    return undefined;
  }, z.boolean().optional()),
  furnished: z
    .enum([...furnishingTypes, "ANY"])
    .optional()
    .catch("ANY"),
  condition: z
    .enum([...conditionTypes, "ANY"])
    .optional()
    .catch("ANY"),
  parkingType: z.enum([...parkingTypes, "ANY"]).optional(),
  petsAllowed: z.enum([...petsAllowedTypes, "ANY"]).optional(),
  type: z
    .preprocess(
      (val) => {
        if (typeof val === "string") return [val];
        if (Array.isArray(val)) return val;
        return val;
      },
      z.array(z.enum(propertyTypes)),
    )
    .optional(),
  priceMin: z.coerce.number().optional(),
  priceMax: z.coerce.number().optional(),
  areaMin: z.coerce.number().optional(),
  areaMax: z.coerce.number().optional(),
  bedroomsMin: z.coerce.number().optional(),
  bedroomsMax: z.coerce.number().optional(),
  volumeMin: z.coerce.number().optional(),
  volumeMax: z.coerce.number().optional(),
  bathroomsMin: z.coerce.number().optional(),
  bathroomsMax: z.coerce.number().optional(),
  floorsMin: z.coerce.number().optional(),
  floorsMax: z.coerce.number().optional(),
  gardenAreaMin: z.coerce.number().optional(),
  gardenAreaMax: z.coerce.number().optional(),
  yearMin: z.coerce.number().optional(),
  yearMax: z.coerce.number().optional(),
  hasBalcony: z.preprocess((val) => {
    if (typeof val === "boolean") return val;
    if (val === "true") return true;
    if (val === "false") return false;
    return undefined;
  }, z.boolean().optional()),
  hasGarden: z.preprocess((val) => {
    if (typeof val === "boolean") return val;
    if (val === "true") return true;
    if (val === "false") return false;
    return undefined;
  }, z.boolean().optional()),
  tenantType: z.enum([...tenantTypes, "ANY"]).optional(),
  energyLabel: z.preprocess((val) => {
    if (typeof val === "string") return [val];
    if (Array.isArray(val)) return val;
    return undefined;
  }, z.array(z.string()).optional()),
  buildingFacilities: z
    .preprocess(
      (val) => {
        if (typeof val === "string") return [val];
        if (Array.isArray(val)) return val;
        return val;
      },
      z.array(z.enum(buildingFacilities)),
    )
    .optional(),
  floorNumber: z.coerce.number().optional(),
  atLeastOneImage: z.preprocess((val) => {
    if (typeof val === "boolean") return val;
    if (val === "true") return true;
    if (val === "false") return false;
    return undefined;
  }, z.boolean().optional()),
});

export type ListingFilters = z.infer<typeof listingFilterSchema>;
